import { BaseRequestConfig } from "lib/api";

import * as constants from "lib/api/constants";
import { Paths } from "lib/api/constants";
import { Store, RootState } from "store";
import * as dotEnv from "lib/dotEnv";

export class ApiPromiseClient {
	constructor(
		private readonly baseUrl: string,
		private readonly store: Store
	) {}

	GetState(): RootState {
		return this.store.getState();
	}

	private getFullUrl(path: string): string {
		return `${this.baseUrl}${path}`;
	}

	public GetBaseUrl(): string {
		return this.baseUrl;
	}

	public static TimeoutAbortSignal(
		timeout = parseInt(dotEnv.Get("API_DEFAULT_TIMEOUT_MS"))
	): AbortSignal {
		const controller = new AbortController();
		setTimeout(() => controller.abort(), timeout);
		return controller.signal;
	}

	private relativeToAbsoluteUrl(
		url: string | ((p: Paths) => string)
	): string {
		const resolved: unknown =
			typeof url === "function" ? url(constants.Paths()) : url;

		if (typeof resolved !== "string") {
			throw new Error("Resolved URL is not a string.");
		}

		// Workaround for TS wrongly thinking .startsWith might be undefined
		if ((resolved as string).startsWith?.("http")) {
			return resolved;
		} else {
			return this.getFullUrl(resolved);
		}
	}

	async Get<T>(
		url: string | ((p: Paths) => string),
		config?: BaseRequestConfig
	): Promise<T> {
		const fullUrl = this.relativeToAbsoluteUrl(url);
		const axios = this.store.getState().token.axiosInstance;
		if (!axios) throw new Error("Axios instance not available");

		return axios
			.get<T>(fullUrl, {
				signal: ApiPromiseClient.TimeoutAbortSignal(),
				...config,
			})
			.then((res) => res.data);
	}

	async Post<T>(
		url: string | ((p: Paths) => string),
		body?: unknown,
		config?: BaseRequestConfig
	): Promise<T> {
		const fullUrl = this.relativeToAbsoluteUrl(url);
		const axios = this.store.getState().token.axiosInstance;
		if (!axios) throw new Error("Axios instance not available");

		return axios
			.post<T>(fullUrl, body, {
				signal: ApiPromiseClient.TimeoutAbortSignal(),
				...config,
			})
			.then((res) => res.data);
	}

	async Put<T>(
		url: string | ((p: Paths) => string),
		body?: unknown,
		config?: BaseRequestConfig
	): Promise<T> {
		const fullUrl = this.relativeToAbsoluteUrl(url);
		const axios = this.store.getState().token.axiosInstance;
		if (!axios) throw new Error("Axios instance not available");

		return axios
			.put<T>(fullUrl, body, {
				signal: ApiPromiseClient.TimeoutAbortSignal(),
				...config,
			})
			.then((res) => res.data);
	}

	async Delete<T>(
		url: string | ((p: Paths) => string),
		config?: BaseRequestConfig
	): Promise<T> {
		const fullUrl = this.relativeToAbsoluteUrl(url);
		const axios = this.store.getState().token.axiosInstance;
		if (!axios) throw new Error("Axios instance not available");

		return axios
			.delete<T>(fullUrl, {
				signal: ApiPromiseClient.TimeoutAbortSignal(),
				...config,
			})
			.then((res) => res.data);
	}
}
